import React, { useState, useEffect, useMemo } from "react";
import "./ClientsCss.css";
import { useNavigate } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../Firebase/firebaseconfig";
import { FaPlus, FaSearch } from "react-icons/fa";

const CLIENTES_POR_PAGINA = 20;

const Clientes = () => {
  const [clientes, setClientes] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [orden, setOrden] = useState("folio");
  const [paginaActual, setPaginaActual] = useState(1);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;

    const fetch = async () => {
      setLoading(true);
      try {
        const snap = await getDocs(collection(db, "clientes"));
        if (!isMounted) return;

        setClientes(snap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      } catch (err) {
        console.error("Error cargando clientes:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetch();

    return () => {
      isMounted = false;
    };
  }, []);

  const filtrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();

    return [...clientes]
      .filter((c) => {
        const nombreCompleto = `${c.nombre || ""} ${c.apellidos || ""}`.toLowerCase();
        const folio = `${c.folio || ""}`.toLowerCase();
        const email = `${c.email || ""}`.toLowerCase();
        const telefono = `${c.telefono || ""}`.toLowerCase();
        const id = `${c.id || ""}`.toLowerCase();

        return (
          nombreCompleto.includes(q) ||
          folio.includes(q) ||
          email.includes(q) ||
          telefono.includes(q) ||
          id.includes(q)
        );
      })
      .sort((a, b) => {
        if (orden === "folio") {
          return `${a.folio || ""}`.localeCompare(`${b.folio || ""}`, undefined, {
            numeric: true,
          });
        }

        return `${a.nombre || ""} ${a.apellidos || ""}`.localeCompare(
          `${b.nombre || ""} ${b.apellidos || ""}`
        );
      });
  }, [clientes, busqueda, orden]);

  const totalPaginas = Math.max(
    1,
    Math.ceil(filtrados.length / CLIENTES_POR_PAGINA)
  );

  useEffect(() => {
    setPaginaActual(1);
  }, [busqueda, orden]);

  useEffect(() => {
    if (paginaActual > totalPaginas) {
      setPaginaActual(totalPaginas);
    }
  }, [paginaActual, totalPaginas]);

  const clientesPagina = useMemo(() => {
    const inicio = (paginaActual - 1) * CLIENTES_POR_PAGINA;
    return filtrados.slice(inicio, inicio + CLIENTES_POR_PAGINA);
  }, [filtrados, paginaActual]);

  if (loading) {
    return <div className="clientes-container">Cargando clientes...</div>;
  }

  return (
    <div className="clientes-container">
      <div className="clientes-page-header">
        <div>
          <p>Clientes</p>
          <h2>Gestion de Clientes</h2>
          <span>
            {filtrados.length} de {clientes.length} clientes visibles
          </span>
        </div>

        <button
          className="nuevo-cliente-button"
          onClick={() => navigate("/app/nuevo-cliente")}
        >
          <FaPlus /> Nuevo Cliente
        </button>
      </div>

      <div className="clientes-header">
        <div className="busqueda-wrap">
          <FaSearch />
          <input
            type="text"
            placeholder="Buscar por nombre, folio, correo, telefono o ID"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="busqueda-input"
          />
        </div>

        <select
          value={orden}
          onChange={(e) => setOrden(e.target.value)}
          className="orden-select"
        >
          <option value="folio">Ordenar por Folio</option>
          <option value="nombre">Ordenar por Nombre</option>
        </select>
      </div>

      <div className="table-wrapper">
        <table className="clientes-table">
          <thead>
            <tr>
              <th>Folio</th>
              <th>Nombre</th>
              <th>Correo</th>
              <th>Teléfono</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {clientesPagina.map((c) => (
              <tr key={c.id}>
                <td data-label="Folio">{c.folio || "-"}</td>
                <td data-label="Nombre">
                  {c.nombre || ""} {c.apellidos || ""}
                </td>
                <td data-label="Correo">{c.email || "-"}</td>
                <td data-label="Teléfono">{c.telefono || "-"}</td>
                <td data-label="Acciones">
                  <button
                    className="detalle-btn"
                    onClick={() => navigate(`/app/cliente/${c.id}`)}
                  >
                    <FaSearch />
                  </button>
                </td>
              </tr>
            ))}

            {clientesPagina.length === 0 && (
              <tr>
                <td colSpan="5" style={{ textAlign: "center", opacity: 0.7 }}>
                  Sin resultados
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="paginador">
        <button
          onClick={() => setPaginaActual((p) => Math.max(1, p - 1))}
          disabled={paginaActual === 1}
        >
          Anterior
        </button>

        <span>
          Página {paginaActual} de {totalPaginas}
        </span>

        <button
          onClick={() => setPaginaActual((p) => Math.min(totalPaginas, p + 1))}
          disabled={paginaActual === totalPaginas}
        >
          Siguiente
        </button>
      </div>
    </div>
  );
};

export default Clientes;